import type { ReactElement } from 'react';

import { Box, Card, Flex, SegmentedControl } from '@rango-dev/whale';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { Info } from '../ProfileMobileMode/Info';

import { DailyChart } from './DailyChart';
import { sortByTotalVolume } from './ProfileUserData.helpers';
import styles from './ProfileUserData.module.css';
import { ProfileUserDataTabs } from './ProfileUserData.types';
import { ProfileUserDataBridgesTab } from './ProfileUserDataBridgesTab';
import { ProfileUserDataChainsTab } from './ProfileUserDataChainsTab';
import { ProfileUserDataDexTab } from './ProfileUserDataDexTab';
import { ProfileUserDataEmptyState } from './ProfileUserDataEmptyState';

export function ProfileUserData(): ReactElement {
  const {
    data: profileDetails,
    loading,
    error,
  } = useAppSelector((state) => state.profile.profileDetails);
  const { t } = useTranslation();
  const logInInProgress = useAppSelector((state) => state.profile.profileAuth.loading);

  const [selectedTab, setSelectedTab] = useState<ProfileUserDataTabs>(ProfileUserDataTabs.Chains);

  const renderTabContent = () => {
    switch (selectedTab) {
      case ProfileUserDataTabs.Chains:
        return (
          <ProfileUserDataChainsTab
            profileChains={profileDetails?.chains.slice().sort(sortByTotalVolume)}
            loading={logInInProgress || loading}
            error={!!error}
          />
        );
      case ProfileUserDataTabs.Bridges:
        return (
          <ProfileUserDataBridgesTab
            profileBridges={profileDetails?.bridges.slice().sort(sortByTotalVolume)}
            loading={logInInProgress || loading}
            error={!!error}
          />
        );
      case ProfileUserDataTabs.DEXes:
        return (
          <ProfileUserDataDexTab
            profileDexes={profileDetails?.dexes.slice().sort(sortByTotalVolume)}
            loading={logInInProgress || loading}
            error={!!error}
          />
        );
      case ProfileUserDataTabs.Campaigns:
        return (
          <ProfileUserDataEmptyState
            error={false}
            defaultMessage={t('Soon, you’ll be able to view the campaigns you previously joined.')}
            image="/imgs/illustrations/profile-campaigns-empty"
          />
        );
      default:
        return <ProfileUserDataChainsTab error={!!error} />;
    }
  };

  const getDailyData = () => {
    switch (selectedTab) {
      case ProfileUserDataTabs.Chains:
        return profileDetails?.chainsDaily;
      case ProfileUserDataTabs.Bridges:
        return profileDetails?.bridgesDaily;
      case ProfileUserDataTabs.DEXes:
        return profileDetails?.dexesDaily;
      case ProfileUserDataTabs.Campaigns:
      default:
        return undefined;
    }
  };

  return (
    <Card variant="surface" size={{ initial: '2', md: '3' }}>
      <Flex direction="column" gap="6">
        <Flex
          direction={{ initial: 'column', sm: 'row' }}
          justify="between"
          align={{ initial: 'stretch', sm: 'start' }}
          gap="4">
          <Info
            title={t('Your Data')}
            description={t('View your stats in every chain, bridge, dex and campaign.')}
          />
          <Box width={{ initial: '100%', sm: 'fit-content' }}>
            <DailyChart loading={loading} dailyData={getDailyData()} />
          </Box>
        </Flex>
        <Box className={styles.scrollContainer}>
          <SegmentedControl.Root
            variant="surface"
            value={selectedTab}
            onValueChange={(value) => setSelectedTab(value as ProfileUserDataTabs)}
            size={{ initial: '1', md: '3' }}>
            <SegmentedControl.Item value={ProfileUserDataTabs.Chains}>
              {t('Chains')}
            </SegmentedControl.Item>
            <SegmentedControl.Item value={ProfileUserDataTabs.Bridges}>
              {t('Bridges')}
            </SegmentedControl.Item>
            <SegmentedControl.Item value={ProfileUserDataTabs.DEXes}>
              {t('DEXes')}
            </SegmentedControl.Item>
            <SegmentedControl.Item value={ProfileUserDataTabs.Campaigns}>
              {t('Campaigns')}
            </SegmentedControl.Item>
          </SegmentedControl.Root>
        </Box>
        {renderTabContent()}
      </Flex>
    </Card>
  );
}
